import displayedPrice from './displayedPrice';
import { finalPrice } from './finalPricing';

/**
 * The three prices a search surface can paint for one pricing object:
 * - `control`: the charged `total`, what every tenant sees today;
 * - `netted`: INS-1288's total without the convenience fee (`displayTotal ?? total`);
 * - `final`: BOOK-168's markup-inclusive total (`totalWithMarkup ?? total`).
 *
 * Each experiment keeps its own reader -- `utils/trips/priceWithoutFeesExperiment` for netted,
 * `utils/trips/finalPriceExperiment` for final -- and the final-arm rule refuses to run under
 * INS-1288, so a user resolves at most one non-control arm.
 */
export const PRICE_ARMS = Object.freeze({
  CONTROL: 'control',
  NETTED: 'netted',
  FINAL: 'final',
});

/**
 * Names the arm from what the two readers resolved.
 *
 * INS-1288 takes precedence (BOOK-168, 2026-09-22): the final-arm rule already skips any payload
 * or tenant under INS-1288, so both at once should be impossible; if it ever happens, the
 * tenant keeps the experiment it already runs.
 *
 * @param {object} [readers] - What each reader resolved.
 * @param {boolean} [readers.netted] - The INS-1288 reader's answer.
 * @param {boolean} [readers.final] - The final-price reader's answer.
 * @returns {string} One of `PRICE_ARMS`.
 */
export const resolvePriceArm = ({ netted = false, final = false } = {}) => {
  if (netted) return PRICE_ARMS.NETTED;
  if (final) return PRICE_ARMS.FINAL;
  return PRICE_ARMS.CONTROL;
};

/**
 * The figure to paint for a pricing object in the given arm.
 *
 * Netted goes through INS-1288's `displayedPrice` unchanged, so the two experiments cannot drift
 * apart on how their own figure is read. A missing or unknown arm paints control -- the same
 * failure mode as `displayedPrice`'s default: forgetting the arm means "no experiment", never a
 * raised or reduced price.
 *
 * @param {{ total?: number, displayTotal?: number, totalWithMarkup?: number }} [pricing] - A
 *   pricing object, a provider-discount entry or a passenger-type entry.
 * @param {string} [arm] - One of `PRICE_ARMS`.
 * @returns {number|string|undefined} The figure to paint, or undefined when there is none.
 */
export const priceForArm = (pricing, arm = PRICE_ARMS.CONTROL) => {
  if (arm === PRICE_ARMS.NETTED) return displayedPrice(pricing, true);
  if (arm === PRICE_ARMS.FINAL) return finalPrice(pricing, true);
  return pricing?.total;
};
